import { toJpeg } from 'html-to-image';

export interface ScreenshotOptions {
  filename?: string;
  quality?: number;
  scale?: number;
  backgroundColor?: string;
  width?: number;
  height?: number;
}

export const takeScreenshot = async (
  element: HTMLElement,
  options: ScreenshotOptions = {},
): Promise<void> => {
  try {
    const {
      filename = 'screenshot.jpg',
      quality = 1,
      scale = 2,
      backgroundColor = '#ffffff',
      width,
      height,
    } = options;

    // Find the actual content container (might be nested)
    let targetElement = element;

    // Look for common scrollable container patterns
    const scrollableSelectors = [
      '[class*="overflow-auto"]',
      '[class*="overflow-scroll"]',
      '[class*="flex-1"]',
      '[class*="content"]',
      '[class*="body"]',
    ];

    for (const selector of scrollableSelectors) {
      const found = element.querySelector(selector) as HTMLElement;
      if (
        found &&
        (found.scrollHeight > found.clientHeight ||
          found.scrollWidth > found.clientWidth)
      ) {
        targetElement = found;
        break;
      }
    }

    // Store original scroll position and overflow
    const originalScrollTop = targetElement.scrollTop;
    const originalScrollLeft = targetElement.scrollLeft;
    const originalOverflow = targetElement.style.overflow;
    const originalHeight = targetElement.style.height;
    const originalMaxHeight = targetElement.style.maxHeight;
    const originalPosition = targetElement.style.position;

    // Temporarily modify element to capture full content
    targetElement.style.overflow = 'visible';
    targetElement.style.height = 'auto';
    targetElement.style.maxHeight = 'none';
    targetElement.style.position = 'relative';
    targetElement.scrollTop = 0;
    targetElement.scrollLeft = 0;

    // Get the full scrollable dimensions
    const scrollHeight = targetElement.scrollHeight;
    const scrollWidth = targetElement.scrollWidth;

    const dataUrl = await toJpeg(element, {
      backgroundColor,
      width: width || scrollWidth,
      height: height || scrollHeight,
      quality,
      pixelRatio: scale,
      cacheBust: true,
    });

    // Convert data URL to blob and download
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;

    // Trigger download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    // Restore original element state
    targetElement.style.overflow = originalOverflow;
    targetElement.style.height = originalHeight;
    targetElement.style.maxHeight = originalMaxHeight;
    targetElement.style.position = originalPosition;
    targetElement.scrollTop = originalScrollTop;
    targetElement.scrollLeft = originalScrollLeft;
  } catch (error) {
    console.error('Error taking screenshot:', error);
    throw error;
  }
};

export const takeScreenshotAsDataUrl = async (
  element: HTMLElement,
  options: Omit<ScreenshotOptions, 'filename'> = {},
): Promise<string> => {
  try {
    const {
      quality = 1,
      scale = 2,
      backgroundColor = '#ffffff',
      width,
      height,
    } = options;

    // Find the actual content container (might be nested)
    let targetElement = element;

    // Look for common scrollable container patterns
    const scrollableSelectors = [
      '[class*="overflow-auto"]',
      '[class*="overflow-scroll"]',
      '[class*="flex-1"]',
      '[class*="content"]',
      '[class*="body"]',
    ];

    for (const selector of scrollableSelectors) {
      const found = element.querySelector(selector) as HTMLElement;
      if (
        found &&
        (found.scrollHeight > found.clientHeight ||
          found.scrollWidth > found.clientWidth)
      ) {
        targetElement = found;
        break;
      }
    }

    // Store original scroll position and overflow
    const originalScrollTop = targetElement.scrollTop;
    const originalScrollLeft = targetElement.scrollLeft;
    const originalOverflow = targetElement.style.overflow;
    const originalHeight = targetElement.style.height;
    const originalMaxHeight = targetElement.style.maxHeight;
    const originalPosition = targetElement.style.position;

    // Temporarily modify element to capture full content
    targetElement.style.overflow = 'visible';
    targetElement.style.height = 'auto';
    targetElement.style.maxHeight = 'none';
    targetElement.style.position = 'relative';
    targetElement.scrollTop = 0;
    targetElement.scrollLeft = 0;

    // Get the full scrollable dimensions
    const scrollHeight = targetElement.scrollHeight;
    const scrollWidth = targetElement.scrollWidth;

    const dataUrl = await toJpeg(element, {
      backgroundColor,
      width: width || scrollWidth,
      height: height || scrollHeight,
      quality,
      pixelRatio: scale,
      cacheBust: true,
    });

    // Restore original element state
    targetElement.style.overflow = originalOverflow;
    targetElement.style.height = originalHeight;
    targetElement.style.maxHeight = originalMaxHeight;
    targetElement.style.position = originalPosition;
    targetElement.scrollTop = originalScrollTop;
    targetElement.scrollLeft = originalScrollLeft;

    return dataUrl;
  } catch (error) {
    console.error('Error taking screenshot:', error);
    throw error;
  }
};
